import { ProjectProps } from "./projectDetails.ts";
import Link from "next/link";
import Image from "next/image";
import AnimatedTitle from "../../animations/AnimatedTitle.tsx";
import AnimatedBody from "../../animations/AnimatedBody.tsx";
import { motion } from "framer-motion";
import Container from "../container/Container.tsx";
import React from "react";
import { SiGithub } from "react-icons/si";
import { BsLink45Deg } from "react-icons/bs";

const ProjectCard = ({
  id,
  name,
  description,
  technologies,
  techNames,
  techLinks,
  github,
  demo,
  image,
  available,
}: ProjectProps) => {
  const hasGithub = Boolean(github);
  const hasDemo = Boolean(demo);
  const showActions = available && (hasGithub || hasDemo || id === 2);

  return (
    <motion.div
      className={`group relative z-10 h-[550px] w-full items-stretch justify-center bg-cover bg-center bg-no-repeat py-0 sm:h-[700px] sm:w-[100%] md:h-[650px] md:w-[100%] lg:h-[500px]`}
      initial="initial"
      animate="animate"
    >
      <Container
        width="100%"
        height="100%"
        borderRadius={25}
        color="rgba(255, 255, 255, 0.1)"
        blur={false}
        grain={true}
        top="0px"
        left="0px"
        angle={0}
      >
        {/* Project detail link overlay */}
        <Link
          href={`/history/v2/projects/${id}`}
          className="absolute inset-0 z-20 bg-black bg-opacity-0 transition-all duration-300 group-hover:bg-opacity-20"
          aria-label={`View ${name} project details`}
        />

        <Image
          src={image}
          alt={name}
          width={500}
          height={500}
          className={`absolute -bottom-2 w-[70%] sm:w-[85%] md:w-[60%] lg:max-w-[55%] ${id % 2 === 0 ? "right-0" : "left-0"
            }`}
          priority={true}
        />
        <div
          className={`absolute top-0 z-30 text-[#0E1016] ${id % 2 === 0 ? "left-0 ml-8 lg:ml-14" : "right-0 mr-8 lg:mr-14"
            } mt-6 flex items-center justify-center gap-4 lg:mt-10`}
        >
          {showActions ? (
            <>
              {hasGithub && (
                <Link
                  href={github}
                  target="_blank"
                  aria-label="Open GitHub Repository"
                  className="w-[43px] rounded-full bg-white p-3 text-[20px] md:w-[65px] md:p-5 md:text-[24px] lg:w-[65px] lg:text-[28px]"
                  data-blobity
                  data-blobity-radius="35"
                  data-blobity-offset-x="4"
                  data-blobity-offset-y="4"
                  data-blobity-magnetic="false"
                >
                  <SiGithub />
                </Link>
              )}
              {hasDemo && (
                <Link
                  href={demo}
                  target="_blank"
                  aria-label="Open Live Demo"
                  className="w-[43px] rounded-full bg-white p-3 text-[20px] md:w-[65px] md:p-5 md:text-[24px] lg:w-[65px] lg:text-[28px]"
                  data-blobity
                  data-blobity-radius="35"
                  data-blobity-offset-x="4"
                  data-blobity-offset-y="4"
                  data-blobity-magnetic="false"
                >
                  <BsLink45Deg />
                </Link>
              )}
              {id === 2 && (
                <Link
                  href="/documate"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download App"
                  className="w-[43px] rounded-full bg-blue-600 p-3 text-[20px] text-white md:w-[65px] md:p-5 md:text-[24px] lg:w-[65px] lg:text-[28px]"
                  data-blobity
                  data-blobity-radius="35"
                  data-blobity-offset-x="4"
                  data-blobity-offset-y="4"
                  data-blobity-magnetic="false"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="h-full w-full"
                  >
                    <path d="M12 1.5a.75.75 0 01.75.75V7.5h-1.5V2.25A.75.75 0 0112 1.5zM11.25 7.5v5.69l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V7.5h3.75a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9a3 3 0 013-3h3.75z" />
                  </svg>
                </Link>
              )}
            </>
          ) : (
            <div></div>
          )}
        </div>
        <div
          className={`absolute z-30 text-white ${!(id % 2 === 0)
              ? "right-0 top-20 mr-0 ml-10 md:right-0 md:ml-0 lg:right-0 lg:top-40 lg:mr-4"
              : "left-10 top-20 ml-0 md:mr-12 lg:top-40 lg:ml-4"
            } mb-10 md:mb-16 lg:mb-14`}
        >
          <AnimatedTitle
            text={name}
            className={
              "max-w-[90%] text-[40px] leading-none text-white md:text-[44px] md:leading-none lg:max-w-[450px] lg:text-[48px] lg:leading-none"
            }
            wordSpace={"mr-[0.25em]"}
            charSpace={"-mr-[0.01em]"}
          />
          <AnimatedBody
            text={description}
            className={
              "mt-4 w-[90%] max-w-[457px] text-[16px] font-semibold text-[#95979D]"
            }
          />
          <div className="col-start-1 col-end-2 mt-9 mb-9 grid grid-cols-5 gap-5">
            {technologies.map(
              (
                IconComponent: React.ComponentType<{ className?: string }>,
                idx: number
              ) => (
                <div key={idx} className={"relative"}>
                  <Link
                    href={techLinks[idx]}
                    target="_blank"
                    aria-label={`Learn more about ${techNames[idx]}`}
                    className="w-[20px] text-[20px] md:w-[25px] md:text-[24px] lg:w-[30px] lg:text-[28px]"
                    title={techLinks[idx]}
                    data-blobity-tooltip={techNames[idx]}
                    data-blobity-magnetic="false"
                  >
                    <IconComponent />
                  </Link>
                </div>
              )
            )}
          </div>
        </div>
      </Container>
    </motion.div>
  );
};

export default ProjectCard;
